import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase, insertWithUserId, handleSupabaseError } from "@/lib/hooks/api/utils";
import type { CreateTaskNote } from "@/lib/schemas/task";
import type { UseCreateTaskNoteOptions } from "./tasks-types";

export function useCreateTaskNote(options?: UseCreateTaskNoteOptions) {
  const queryClient = useQueryClient();
  const invalidateScope = options?.invalidateScope ?? "full";

  return useMutation({
    mutationFn: async (noteData: CreateTaskNote) => {
      const { data, error } = await insertWithUserId("task_notes", noteData);

      if (error) {
        handleSupabaseError(error, "create task note");
      }

      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["task-notes", data.task_id] });
      if (invalidateScope === "full") {
        // Also invalidate broader task caches when callers need full consistency
        void queryClient.invalidateQueries({ queryKey: ["task", data.task_id] });
        void queryClient.invalidateQueries({ queryKey: ["tasks"] });
        void queryClient.invalidateQueries({ queryKey: ["tasks-paginated"] });
      }
      toast.success("Note added successfully");
    },
    onError: (error: Error) => {
      console.error("Error creating task note:", error);
      toast.error("Failed to add note: " + error.message);
    },
  });
}

export function useUpdateTaskNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteData: {
      id: string;
      content: string;
      note_type?: string;
      is_internal?: boolean;
    }) => {
      const supabase = getSupabase();
      const { id, ...updateData } = noteData;

      const { data, error } = await supabase
        .from("task_notes")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("Error updating task note:", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["task-notes", data.task_id] });
      void queryClient.invalidateQueries({ queryKey: ["task", data.task_id] });
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      void queryClient.invalidateQueries({ queryKey: ["tasks-paginated"] });
      toast.success("Note updated successfully");
    },
    onError: (error: Error) => {
      console.error("Error updating task note:", error);
      toast.error("Failed to update note: " + error.message);
    },
  });
}

export function useDeleteTaskNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: string) => {
      const supabase = getSupabase();

      // Fetch task_id before deleting to use in cache invalidation
      const { data: note } = await supabase
        .from("task_notes")
        .select("task_id")
        .eq("id", noteId)
        .single();

      const { error } = await supabase.from("task_notes").delete().eq("id", noteId);

      if (error) {
        console.error("Error deleting task note:", error);
        throw new Error(error.message);
      }

      return { id: noteId, task_id: note?.task_id };
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["task-notes", data.task_id] });
      void queryClient.invalidateQueries({ queryKey: ["task", data.task_id] });
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      void queryClient.invalidateQueries({ queryKey: ["tasks-paginated"] });
      toast.success("Note deleted successfully");
    },
    onError: (error: Error) => {
      console.error("Error deleting task note:", error);
      toast.error("Failed to delete note: " + error.message);
    },
  });
}
